import { useEffect, useState, useCallback, useMemo } from "react";
import { api, formatApiError } from "@/lib/api";
import Layout from "@/components/Layout";
import ConfirmDelete from "@/components/ConfirmDelete";
import { Plus, X, ArrowUpRight, ArrowDownLeft, Boxes, CalendarRange, IndianRupee, Package, Receipt } from "lucide-react";
import { toast, Toaster } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { AreaChart, Area, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

const todayStr = () => new Date().toISOString().slice(0, 10);
const nowTime = () => new Date().toTimeString().slice(0, 5);
const daysAgo = (n) => new Date(Date.now() - n * 86400000).toISOString().slice(0, 10);

export default function Buffing() {
  const { user, isSuper, canSeePrices } = useAuth();
  const isBackOffice = isSuper || user?.role === "admin";
  const showPrices = canSeePrices;

  const [tab, setTab] = useState("stock");
  const [stock, setStock] = useState({ kora: [], raw_materials: [] });
  const [inward, setInward] = useState([]);
  const [outward, setOutward] = useState([]);
  const [weekly, setWeekly] = useState(null);
  const [labour, setLabour] = useState([]);
  const [links, setLinks] = useState([]);
  const [invItems, setInvItems] = useState([]);
  const [processes, setProcesses] = useState([]);

  const [showOut, setShowOut] = useState(false);
  const [showIn, setShowIn] = useState(false);
  const [outForm, setOutForm] = useState({ date: todayStr(), time: nowTime(), type: "kora", model: "", item_id: "", qty: "", crates: "" });
  const [inForm, setInForm] = useState({ date: todayStr(), time: nowTime(), model: "", good_qty: "", burn_mark: "", bottom_open: "", crates: "" });
  const [opening, setOpening] = useState({ model: "", qty: "" });
  const [billing, setBilling] = useState(null);
  const [billRange, setBillRange] = useState({ start: daysAgo(29), end: todayStr() });

  const loadBilling = useCallback((r) => {
    if (!showPrices) return;
    api.get("/buffing/billing", { params: { start: r.start, end: r.end } }).then((res) => setBilling(res.data)).catch(() => {});
  }, [showPrices]);

  const load = useCallback(() => Promise.all([
    api.get("/buffing/stock"), api.get("/buffing/inward"), api.get("/buffing/outward"),
    api.get("/buffing/weekly"), api.get("/inventory-items"), api.get("/buffing/links"),
    showPrices ? api.get("/buffing/labour") : Promise.resolve({ data: [] }),
  ]).then(([s, i, o, w, inv, lk, l]) => {
    setStock(s.data); setInward(i.data); setOutward(o.data); setWeekly(w.data); setInvItems(inv.data); setLinks(lk.data); setLabour(l.data);
  }), [showPrices]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/processes").then((r) => setProcesses(r.data)).catch(() => {}); }, []);

  const koraModels = useMemo(() => invItems.filter((x) => /kora/i.test(x.name)), [invItems]);
  const buffMaterials = useMemo(() => invItems.filter((x) => x.category === "Buffing Material"), [invItems]);
  const inTotal = (+inForm.good_qty || 0) + (+inForm.burn_mark || 0) + (+inForm.bottom_open || 0);

  const saveOutward = async () => {
    try {
      const isModelType = outForm.type === "kora" || outForm.type === "repolish";
      const payload = { date: outForm.date, time: outForm.time, type: outForm.type,
        model: isModelType ? outForm.model : null,
        item_id: outForm.type === "raw_material" ? outForm.item_id : null,
        qty: +outForm.qty || 0, crates: +outForm.crates || 0 };
      if (isModelType && !payload.model) return toast.error("Select a model.");
      if (outForm.type === "raw_material" && !payload.item_id) return toast.error("Select a raw material.");
      await api.post("/buffing/outward", payload);
      toast.success(outForm.type === "repolish" ? "Repolish sent · SEMI-FG deducted · receivable added" : "Outward recorded"); setShowOut(false); setOutForm({ date: todayStr(), time: nowTime(), type: "kora", model: "", item_id: "", qty: "", crates: "" }); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const saveInward = async () => {
    try {
      if (!inForm.model) return toast.error("Select a Kora model.");
      await api.post("/buffing/inward", { date: inForm.date, time: inForm.time, model: inForm.model,
        good_qty: +inForm.good_qty || 0, burn_mark: +inForm.burn_mark || 0, bottom_open: +inForm.bottom_open || 0, crates: +inForm.crates || 0 });
      toast.success("Inward recorded · auto-saved to DPR"); setShowIn(false); setInForm({ date: todayStr(), time: nowTime(), model: "", good_qty: "", burn_mark: "", bottom_open: "", crates: "" }); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const saveOpening = async () => {
    try {
      if (!opening.model) return toast.error("Select a Kora model.");
      await api.post("/buffing/opening", { model: opening.model, qty: +opening.qty || 0 });
      toast.success("Opening stock set"); setOpening({ model: "", qty: "" }); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const saveLabour = async (model, rate) => {
    try { await api.post("/buffing/labour", { model, rate_per_pc: +rate || 0 }); toast.success("Labour rate saved"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const saveMaterialRate = async (item_id, rate) => {
    try { await api.post("/buffing/material-rate", { item_id, rate: +rate || 0 }); toast.success("Material rate saved"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const saveLink = async (kora_model, output_item_id, packing_process_id) => {
    if (!output_item_id) return;
    try { await api.post("/buffing/links", { kora_model, output_item_id, packing_process_id: packing_process_id || null }); toast.success("Output link saved"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const delOut = async (id) => { try { await api.delete(`/buffing/outward/${id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(formatApiError(e)); } };
  const delIn = async (id) => { try { await api.delete(`/buffing/inward/${id}`); toast.success("Deleted · DPR reversed"); load(); } catch (e) { toast.error(formatApiError(e)); } };

  const tabs = [["stock", "Buffing Stock", Boxes], ["outward", "Outward", ArrowUpRight], ["inward", "Inward", ArrowDownLeft], ["weekly", "Weekly Report", CalendarRange]];
  if (showPrices) { tabs.push(["billing", "Billing & Dashboard", Receipt]); tabs.push(["cost", "Cost", IndianRupee]); }
  const unitCostOf = (item_id) => invItems.find((x) => x.id === item_id)?.unit_cost || 0;

  return (
    <Layout title="Buffing (Job Work)" subtitle="Kora → Polished · inward/outward · stock with buffing team"
      actions={
        <div className="flex gap-2">
          <button data-testid="buff-outward-btn" onClick={() => setShowOut(true)} className="bg-white border border-slate-300 hover:border-amber-500 text-slate-700 px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2"><ArrowUpRight size={16} /> Outward</button>
          <button data-testid="buff-inward-btn" onClick={() => setShowIn(true)} className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2"><ArrowDownLeft size={16} /> Inward</button>
        </div>
      }>
      <Toaster richColors position="top-right" />

      <div className="flex gap-2 mb-5 border-b border-slate-200">
        {tabs.map(([k, l, Icon]) => (
          <button key={k} data-testid={`buff-tab-${k}`} onClick={() => setTab(k)}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px ${tab === k ? "border-blue-600 text-blue-700" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
            <Icon size={15} /> {l}
          </button>
        ))}
      </div>

      {tab === "stock" && (
        <div className="space-y-6">
          <div className="section-card">
            <h3 className="font-heading font-semibold mb-3 flex items-center gap-2"><Package size={18} className="text-blue-600" /> Kora Bottles with Buffing Team</h3>
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
                <tr><th className="text-left py-2 px-3">Model</th><th className="text-right py-2 px-3">Opening</th><th className="text-right py-2 px-3">Sent (Outward)</th><th className="text-right py-2 px-3">Returned (Inward)</th><th className="text-right py-2 px-3">Held Now</th><th className="text-right py-2 px-3">Repolish Receivable</th></tr>
              </thead>
              <tbody data-testid="buff-stock-body">
                {stock.kora.length === 0 && <tr><td colSpan="6" className="text-center py-8 text-slate-400">No buffing stock yet. Set opening stock or record an outward.</td></tr>}
                {stock.kora.map((k) => (
                  <tr key={k.model} className="border-t border-slate-100">
                    <td className="py-2 px-3 font-medium">{k.model}</td>
                    <td className="py-2 px-3 text-right font-mono">{k.opening.toLocaleString()}</td>
                    <td className="py-2 px-3 text-right font-mono text-amber-700">{k.sent.toLocaleString()}</td>
                    <td className="py-2 px-3 text-right font-mono text-emerald-700">{k.returned.toLocaleString()}</td>
                    <td className="py-2 px-3 text-right font-mono font-bold">{k.held.toLocaleString()}</td>
                    <td data-testid={`repolish-recv-${k.model}`} className={`py-2 px-3 text-right font-mono font-semibold ${(k.repolish_receivable || 0) > 0 ? "text-orange-600" : "text-slate-400"}`}>{(k.repolish_receivable || 0).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {stock.raw_materials.length > 0 && (
            <div className="section-card">
              <h3 className="font-heading font-semibold mb-3">Raw Material to be Billed to Buffing Team <span className="text-xs font-normal text-slate-400">(consumed, not returned)</span></h3>
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider"><tr><th className="text-left py-2 px-3">Item</th><th className="text-right py-2 px-3">Qty Consumed</th>{showPrices && <th className="text-right py-2 px-3">Rate</th>}{showPrices && <th className="text-right py-2 px-3">Value (₹)</th>}</tr></thead>
                <tbody>{stock.raw_materials.map((r) => <tr key={r.item_id} className="border-t border-slate-100"><td className="py-2 px-3">{r.name}</td><td className="py-2 px-3 text-right font-mono">{r.held.toLocaleString()} {r.unit}</td>{showPrices && <td className="py-2 px-3 text-right font-mono">₹ {unitCostOf(r.item_id)}</td>}{showPrices && <td className="py-2 px-3 text-right font-mono font-semibold">₹ {(r.held * unitCostOf(r.item_id)).toLocaleString()}</td>}</tr>)}</tbody>
              </table>
            </div>
          )}

          {isSuper && (
            <div className="section-card">
              <h3 className="font-heading font-semibold mb-3">Set Opening Stock (one-time, per model)</h3>
              <div className="flex flex-wrap items-end gap-3">
                <div><label className="block text-xs font-medium text-slate-600 mb-1">Kora Model</label>
                  <select data-testid="buff-opening-model" value={opening.model} onChange={(e) => setOpening({ ...opening, model: e.target.value })} className="border border-slate-300 rounded-lg px-3 py-2 text-sm w-72"><option value="">Select…</option>{koraModels.map((m) => <option key={m.id} value={m.name}>{m.name}</option>)}</select></div>
                <div><label className="block text-xs font-medium text-slate-600 mb-1">Opening Qty</label>
                  <input data-testid="buff-opening-qty" type="number" value={opening.qty} onChange={(e) => setOpening({ ...opening, qty: e.target.value })} className="border border-slate-300 rounded-lg px-3 py-2 text-sm w-40" /></div>
                <button data-testid="buff-opening-save" onClick={saveOpening} className="bg-slate-900 hover:bg-slate-800 text-white px-4 py-2 rounded-lg text-sm font-semibold">Save Opening</button>
              </div>
            </div>
          )}
        </div>
      )}

      {tab === "outward" && (
        <div className="section-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider"><tr><th className="text-left py-3 px-3">Date</th><th className="text-left py-3 px-3">Time</th><th className="text-left py-3 px-3">Type</th><th className="text-left py-3 px-3">Item / Model</th><th className="text-right py-3 px-3">Qty</th><th className="text-right py-3 px-3">Crates</th><th></th></tr></thead>
            <tbody data-testid="buff-outward-body">
              {outward.length === 0 && <tr><td colSpan="7" className="text-center py-10 text-slate-400">No outward records.</td></tr>}
              {outward.map((o) => (
                <tr key={o.id} className="border-t border-slate-100 hover:bg-slate-50/50">
                  <td className="py-2 px-3 font-medium">{o.date}</td><td className="py-2 px-3">{o.time}</td>
                  <td className="py-2 px-3"><span className={`stat-pill ${o.type === "kora" ? "bg-blue-50 text-blue-700" : o.type === "repolish" ? "bg-orange-50 text-orange-700" : "bg-purple-50 text-purple-700"}`}>{o.type === "kora" ? "Kora" : o.type === "repolish" ? "Repolish" : "Raw Material"}</span></td>
                  <td className="py-2 px-3">{o.type === "raw_material" ? (invItems.find((x) => x.id === o.item_id)?.name || o.item_id) : o.model}</td>
                  <td className="py-2 px-3 text-right font-mono">{o.qty?.toLocaleString()}</td>
                  <td className="py-2 px-3 text-right font-mono">{o.crates || 0}</td>
                  <td className="py-2 px-3 text-right">{isBackOffice && <ConfirmDelete testId={`del-out-${o.id}`} onConfirm={() => delOut(o.id)} title="Delete outward?" description="Stock will be returned to inventory." />}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "inward" && (
        <div className="section-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider"><tr><th className="text-left py-3 px-3">Date</th><th className="text-left py-3 px-3">Time</th><th className="text-left py-3 px-3">Model</th><th className="text-right py-3 px-3">Good</th><th className="text-right py-3 px-3">Repolish Settled</th><th className="text-right py-3 px-3">Burn Mark</th><th className="text-right py-3 px-3">Bottom Open</th><th className="text-right py-3 px-3">Total</th><th className="text-right py-3 px-3">Crates</th><th></th></tr></thead>
            <tbody data-testid="buff-inward-body">
              {inward.length === 0 && <tr><td colSpan="10" className="text-center py-10 text-slate-400">No inward records.</td></tr>}
              {inward.map((i) => (
                <tr key={i.id} className="border-t border-slate-100 hover:bg-slate-50/50">
                  <td className="py-2 px-3 font-medium">{i.date}</td><td className="py-2 px-3">{i.time}</td><td className="py-2 px-3">{i.model}</td>
                  <td className="py-2 px-3 text-right font-mono text-emerald-700">{i.good_qty?.toLocaleString()}</td>
                  <td data-testid={`in-repolish-settled-${i.id}`} className={`py-2 px-3 text-right font-mono ${(i.repolish_settled || 0) > 0 ? "text-orange-600 font-semibold" : "text-slate-300"}`}>{(i.repolish_settled || 0).toLocaleString()}</td>
                  <td className="py-2 px-3 text-right font-mono text-red-600">{i.burn_mark?.toLocaleString()}</td>
                  <td className="py-2 px-3 text-right font-mono text-orange-600">{i.bottom_open?.toLocaleString()}</td>
                  <td className="py-2 px-3 text-right font-mono font-bold">{i.total_qty?.toLocaleString()}</td>
                  <td className="py-2 px-3 text-right font-mono">{i.crates || 0}</td>
                  <td className="py-2 px-3 text-right">{isBackOffice && <ConfirmDelete testId={`del-in-${i.id}`} onConfirm={() => delIn(i.id)} title="Delete inward?" description="The linked DPR entry and polished stock will be reversed." />}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "weekly" && (
        <div className="section-card overflow-x-auto">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-heading font-semibold">Pieces Buffed — Last 7 Days (Model-wise)</h3>
            {weekly && <span className="text-xs text-slate-500">From {weekly.week_start} · Grand Total <b>{weekly.grand_total?.toLocaleString()}</b> pcs</span>}
          </div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider"><tr><th className="text-left py-2 px-3">Model</th><th className="text-right py-2 px-3">Good</th><th className="text-right py-2 px-3">Burn Mark</th><th className="text-right py-2 px-3">Bottom Open</th><th className="text-right py-2 px-3">Total</th><th className="text-right py-2 px-3">Rejection %</th></tr></thead>
            <tbody data-testid="buff-weekly-body">
              {(!weekly || weekly.models.length === 0) && <tr><td colSpan="6" className="text-center py-8 text-slate-400">No buffing this week.</td></tr>}
              {weekly?.models.map((m) => (
                <tr key={m.model} className="border-t border-slate-100">
                  <td className="py-2 px-3 font-medium">{m.model}</td>
                  <td className="py-2 px-3 text-right font-mono text-emerald-700">{m.good.toLocaleString()}</td>
                  <td className="py-2 px-3 text-right font-mono text-red-600">{m.burn_mark.toLocaleString()}</td>
                  <td className="py-2 px-3 text-right font-mono text-orange-600">{m.bottom_open.toLocaleString()}</td>
                  <td className="py-2 px-3 text-right font-mono font-bold">{m.total.toLocaleString()}</td>
                  <td className="py-2 px-3 text-right"><span className="stat-pill bg-amber-100 text-amber-700">{m.rejection_pct}%</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "cost" && showPrices && (
        <div className="space-y-6">
          <div className="section-card">
            <h3 className="font-heading font-semibold mb-3 flex items-center gap-2"><IndianRupee size={18} className="text-emerald-600" /> Labour Rate per Model (₹/pc) — Admin only</h3>
            <p className="text-xs text-slate-500 mb-4">Buffing labour rate per piece for each Kora model. Not visible to operators.</p>
            <div className="space-y-2 max-w-2xl">
              {koraModels.map((m) => {
                const cur = labour.find((l) => l.model === m.name)?.rate_per_pc ?? "";
                return <RateRow key={m.id} testId={`labour-${m.name}`} saveTestId={`labour-save-${m.name}`} label={m.name} initial={cur} onSave={(v) => saveLabour(m.name, v)} />;
              })}
            </div>
          </div>
          <div className="section-card">
            <h3 className="font-heading font-semibold mb-3">Buffing Raw Material Rate (₹/unit) — billed to buffing team</h3>
            <p className="text-xs text-slate-500 mb-4">Rate at which consumed raw material is billed to the buffing team (deducted from their payout).</p>
            <div className="space-y-2 max-w-2xl">
              {buffMaterials.length === 0 && <div className="text-sm text-slate-400">No items flagged "Buffing Material" yet. Set the flag in Inventory.</div>}
              {buffMaterials.map((m) => (
                <RateRow key={m.id} testId={`matrate-${m.id}`} saveTestId={`matrate-save-${m.id}`} label={`${m.name} (${m.base_unit})`} initial={m.unit_cost ?? ""} onSave={(v) => saveMaterialRate(m.id, v)} />
              ))}
            </div>
          </div>
          <div className="section-card">
            <h3 className="font-heading font-semibold mb-3">Buffing Output Links — Kora → existing inventory item → Packing process</h3>
            <p className="text-xs text-slate-500 mb-4">When a Kora model is inwarded after polishing, its Good qty is added to the LINKED existing item below (no new item is created). Optionally pick the <b>Packing process</b> so this stock shows as that process's available stock in the DPR. Inward is blocked until an output item is set.</p>
            <div className="space-y-2 max-w-4xl" data-testid="buff-links">
              {koraModels.length === 0 && <div className="text-sm text-slate-400">No Kora models found in inventory.</div>}
              {koraModels.map((m) => {
                const link = links.find((l) => l.kora_model === m.name) || {};
                const cur = link.output_item_id || "";
                const curProc = link.packing_process_id || "";
                return (
                  <div key={m.id} className="flex items-center gap-3 flex-wrap">
                    <div className="flex-1 min-w-[140px] text-sm text-slate-700">{m.name}</div>
                    <span className="text-slate-400 text-sm">→</span>
                    <select data-testid={`link-${m.id}`} value={cur} onChange={(e) => saveLink(m.name, e.target.value, curProc)} className="border border-slate-300 rounded-lg px-3 py-1.5 text-sm w-64">
                      <option value="">Select output item…</option>
                      {invItems.filter((x) => !/kora/i.test(x.name)).map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
                    </select>
                    <span className="text-slate-400 text-sm">⇒</span>
                    <select data-testid={`link-proc-${m.id}`} value={curProc} onChange={(e) => saveLink(m.name, cur, e.target.value)} disabled={!cur} className="border border-slate-300 rounded-lg px-3 py-1.5 text-sm w-52 disabled:opacity-50">
                      <option value="">Packing process (optional)…</option>
                      {processes.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {tab === "billing" && showPrices && (
        <div className="space-y-6">
          <div className="section-card flex flex-wrap items-center gap-3" data-testid="buff-bill-filter">
            <span className="text-sm font-semibold text-slate-700">Period:</span>
            <input data-testid="bill-start" type="date" max={todayStr()} value={billRange.start} onChange={(e) => setBillRange({ ...billRange, start: e.target.value })} className="border border-slate-300 rounded-lg px-3 py-1.5 text-sm" />
            <span className="text-slate-400 text-xs">→</span>
            <input data-testid="bill-end" type="date" max={todayStr()} value={billRange.end} onChange={(e) => setBillRange({ ...billRange, end: e.target.value })} className="border border-slate-300 rounded-lg px-3 py-1.5 text-sm" />
            {[["Week", 6], ["Month", 29], ["Year", 364]].map(([l, n]) => (
              <button key={l} data-testid={`bill-preset-${l.toLowerCase()}`} onClick={() => setBillRange({ start: daysAgo(n), end: todayStr() })} className="text-xs font-medium px-2.5 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700">{l}</button>
            ))}
            <button data-testid="bill-run-btn" onClick={() => loadBilling(billRange)} className="bg-slate-900 hover:bg-slate-800 text-white px-4 py-1.5 rounded-lg text-sm font-semibold flex items-center gap-1"><Receipt size={14} /> Bill</button>
          </div>

          {billing && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                <Kpi label="Total Production (pcs)" value={billing.total_production?.toLocaleString()} />
                <Kpi label="Labour Payable" value={`₹ ${billing.labour_payable?.toLocaleString()}`} />
                <Kpi label="Raw Material Billed" value={`₹ ${billing.raw_material_total?.toLocaleString()}`} />
                <Kpi label="Net Payable to Team" value={`₹ ${billing.net_payable?.toLocaleString()}`} accent />
              </div>

              <div className="section-card">
                <h3 className="font-heading font-semibold mb-3">Daily Production ({billing.start} → {billing.end})</h3>
                <div className="h-64">
                  <ResponsiveContainer>
                    <AreaChart data={billing.daily_production}>
                      <defs><linearGradient id="buffg" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#2563EB" stopOpacity={0.5} /><stop offset="100%" stopColor="#2563EB" stopOpacity={0} /></linearGradient></defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis dataKey="date" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} />
                      <Tooltip contentStyle={{ background: "#0F172A", color: "#fff", border: "none", borderRadius: 8, fontSize: 12 }} />
                      <Area type="monotone" dataKey="good" stroke="#2563EB" fill="url(#buffg)" strokeWidth={2.5} name="Good pcs" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="section-card overflow-x-auto">
                <h3 className="font-heading font-semibold mb-3">Production & Labour by Model</h3>
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider"><tr><th className="text-left py-2 px-3">Model</th><th className="text-right py-2 px-3">Good</th><th className="text-right py-2 px-3">Rate ₹/pc</th><th className="text-right py-2 px-3">Labour Payable</th></tr></thead>
                  <tbody data-testid="buff-bill-body">
                    {billing.models.length === 0 && <tr><td colSpan="4" className="text-center py-6 text-slate-400">No production in this period.</td></tr>}
                    {billing.models.map((m) => (
                      <tr key={m.model} className="border-t border-slate-100"><td className="py-2 px-3 font-medium">{m.model}</td><td className="py-2 px-3 text-right font-mono text-emerald-700">{m.good.toLocaleString()}</td><td className="py-2 px-3 text-right font-mono">₹ {m.rate_per_pc}</td><td className="py-2 px-3 text-right font-mono font-semibold">₹ {m.labour_payable.toLocaleString()}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {billing.raw_material_billed.length > 0 && (
                <div className="section-card overflow-x-auto">
                  <h3 className="font-heading font-semibold mb-3">Raw Material Billed to Buffing Team</h3>
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider"><tr><th className="text-left py-2 px-3">Item</th><th className="text-right py-2 px-3">Qty</th><th className="text-right py-2 px-3">Rate</th><th className="text-right py-2 px-3">Value</th></tr></thead>
                    <tbody>{billing.raw_material_billed.map((r) => <tr key={r.item_id} className="border-t border-slate-100"><td className="py-2 px-3">{r.name}</td><td className="py-2 px-3 text-right font-mono">{r.qty.toLocaleString()} {r.unit}</td><td className="py-2 px-3 text-right font-mono">₹ {r.rate}</td><td className="py-2 px-3 text-right font-mono font-semibold">₹ {r.value.toLocaleString()}</td></tr>)}</tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {showOut && (
        <Modal title="Outward to Buffing" onClose={() => setShowOut(false)}>
          <div className="grid grid-cols-2 gap-4">
            <Fld label="Date"><input data-testid="out-date" type="date" value={outForm.date} onChange={(e) => setOutForm({ ...outForm, date: e.target.value })} className="inp" /></Fld>
            <Fld label="Time"><input data-testid="out-time" type="time" value={outForm.time} onChange={(e) => setOutForm({ ...outForm, time: e.target.value })} className="inp" /></Fld>
            <Fld label="Outward Type" full>
              <div className="flex gap-2">
                {[["kora", "Kora Bottle"], ["raw_material", "Raw Material"], ["repolish", "Repolish (unpaid)"]].map(([v, l]) => (
                  <button key={v} data-testid={`out-type-${v}`} onClick={() => setOutForm({ ...outForm, type: v })} className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium border ${outForm.type === v ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-700 border-slate-300"}`}>{l}</button>
                ))}
              </div>
            </Fld>
            {outForm.type === "kora" ? (
              <Fld label="Kora Model" full><select data-testid="out-model" value={outForm.model} onChange={(e) => setOutForm({ ...outForm, model: e.target.value })} className="inp"><option value="">Select…</option>{koraModels.map((m) => <option key={m.id} value={m.name}>{m.name} (stock {m.current_stock})</option>)}</select></Fld>
            ) : outForm.type === "repolish" ? (
              <Fld label="Model (sends its SEMI-FG bottles back for rework)" full>
                <select data-testid="out-repolish-model" value={outForm.model} onChange={(e) => setOutForm({ ...outForm, model: e.target.value })} className="inp"><option value="">Select…</option>{koraModels.map((m) => <option key={m.id} value={m.name}>{m.name}</option>)}</select>
                <p className="text-xs text-orange-600 mt-1.5">Deducts the linked SEMI-FG stock and adds it to receivable from the buffing team. Not paid (their rejection). Auto-settled when they return good pieces.</p>
              </Fld>
            ) : (
              <Fld label="Raw Material (Buffing Material)" full><select data-testid="out-item" value={outForm.item_id} onChange={(e) => setOutForm({ ...outForm, item_id: e.target.value })} className="inp"><option value="">Select…</option>{buffMaterials.map((m) => <option key={m.id} value={m.id}>{m.name} (stock {m.current_stock} {m.base_unit})</option>)}</select></Fld>
            )}
            <Fld label="Quantity"><input data-testid="out-qty" type="number" value={outForm.qty} onChange={(e) => setOutForm({ ...outForm, qty: e.target.value })} className="inp" /></Fld>
            {outForm.type === "kora" && <Fld label="Total Crates"><input data-testid="out-crates" type="number" value={outForm.crates} onChange={(e) => setOutForm({ ...outForm, crates: e.target.value })} className="inp" /></Fld>}
          </div>
          <ModalFooter onCancel={() => setShowOut(false)} onSave={saveOutward} testId="out-save" />
        </Modal>
      )}

      {showIn && (
        <Modal title="Inward from Buffing" onClose={() => setShowIn(false)}>
          <div className="grid grid-cols-2 gap-4">
            <Fld label="Date"><input data-testid="in-date" type="date" value={inForm.date} onChange={(e) => setInForm({ ...inForm, date: e.target.value })} className="inp" /></Fld>
            <Fld label="Time"><input data-testid="in-time" type="time" value={inForm.time} onChange={(e) => setInForm({ ...inForm, time: e.target.value })} className="inp" /></Fld>
            <Fld label="Kora Model" full><select data-testid="in-model" value={inForm.model} onChange={(e) => setInForm({ ...inForm, model: e.target.value })} className="inp"><option value="">Select…</option>{koraModels.map((m) => <option key={m.id} value={m.name}>{m.name}</option>)}</select></Fld>
            <Fld label="Good Quantity"><input data-testid="in-good" type="number" value={inForm.good_qty} onChange={(e) => setInForm({ ...inForm, good_qty: e.target.value })} className="inp" /></Fld>
            <Fld label="Burn Mark (Buffing reject)"><input data-testid="in-burn" type="number" value={inForm.burn_mark} onChange={(e) => setInForm({ ...inForm, burn_mark: e.target.value })} className="inp" /></Fld>
            <Fld label="Bottom Open (Welding reject)"><input data-testid="in-bottom" type="number" value={inForm.bottom_open} onChange={(e) => setInForm({ ...inForm, bottom_open: e.target.value })} className="inp" /></Fld>
            <Fld label="Total Crates"><input data-testid="in-crates" type="number" value={inForm.crates} onChange={(e) => setInForm({ ...inForm, crates: e.target.value })} className="inp" /></Fld>
            <div className="col-span-2 bg-blue-50 border border-blue-200 rounded-lg p-3 flex justify-between">
              <span className="text-sm text-blue-800">Total Quantity (auto):</span>
              <span data-testid="in-total" className="font-heading font-bold text-blue-700">{inTotal.toLocaleString()} pcs</span>
            </div>
            <p className="col-span-2 text-xs text-slate-500">Good qty becomes Polished Bottle production in DPR. Burn Mark + Bottom Open are recorded as rejection in DPR (shown separately here).</p>
          </div>
          <ModalFooter onCancel={() => setShowIn(false)} onSave={saveInward} testId="in-save" />
        </Modal>
      )}
      <style>{`.inp{width:100%;border:1px solid #cbd5e1;border-radius:.5rem;padding:.5rem .75rem;font-size:.875rem}`}</style>
    </Layout>
  );
}

function RateRow({ label, initial, onSave, testId, saveTestId }) {
  const [val, setVal] = useState(initial);
  return (
    <div className="flex items-center gap-3">
      <div className="flex-1 text-sm text-slate-700">{label}</div>
      <input data-testid={testId} type="number" step="0.01" value={val} onChange={(e) => setVal(e.target.value)} placeholder="₹" className="border border-slate-300 rounded-lg px-3 py-1.5 text-sm w-32" />
      <button data-testid={saveTestId} onClick={() => onSave(val)} className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-lg text-sm font-medium">Save</button>
    </div>
  );
}

function Kpi({ label, value, accent }) {
  return (
    <div className={`kpi-card ${accent ? "kpi-success" : "kpi-accent"}`}>
      <div className="text-xs uppercase font-semibold text-slate-500">{label}</div>
      <div className="font-heading text-2xl font-bold text-slate-900 mt-2">{value}</div>
    </div>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b sticky top-0 bg-white"><h2 className="font-heading text-xl font-semibold">{title}</h2><button onClick={onClose}><X /></button></div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

function ModalFooter({ onCancel, onSave, testId }) {
  return (
    <div className="flex justify-end gap-2 mt-5 pt-4 border-t">
      <button onClick={onCancel} className="px-4 py-2 border rounded-lg text-sm font-medium bg-white">Cancel</button>
      <button data-testid={testId} onClick={onSave} className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold">Save</button>
    </div>
  );
}

function Fld({ label, children, full }) { return <div className={full ? "col-span-2" : ""}><label className="block text-sm font-medium text-slate-700 mb-1.5">{label}</label>{children}</div>; }
